import { Alert } from '@amore/ui/components/alert'
import { buttonStyles } from '@amore/ui/components/button'
import { Card } from '@amore/ui/components/card'
import { Select, SelectItem } from '@amore/ui/components/select'
import { Link } from '@tanstack/react-router'
import { Eye, MapPin } from 'lucide-react'
import type { ReactNode } from 'react'
import { formatDate, plural } from '@/lib/format'
import type { ScheduleEvent, WeddingData } from '@/mock/types'
import { guestAudiences, scheduleGroups, wholeDay } from './model'

/** Guest events of the invited parts; guest events before the first part are for everyone. */
function visibleTo(event: ScheduleEvent, parts: string[]) {
  return event.audience === 'guests' && (!event.dayPartId || parts.includes(event.dayPartId))
}

/**
 * The schedule the way guests see it on the wedding website after "Kdo jste?":
 * only guest events of the parts their invitation covers, no team details.
 */
export function GuestView({
  data,
  audienceKey,
  onAudienceChange,
  onOpen,
  actions,
}: {
  data: WeddingData
  audienceKey: string
  onAudienceChange: (key: string) => void
  onOpen?: (event: ScheduleEvent) => void
  /** The view switch, on the right of the toolbar. */
  actions?: ReactNode
}) {
  const audiences = guestAudiences(data)
  const audience = audiences.find((item) => item.key === audienceKey) ?? audiences[0]
  const parts = audience?.parts ?? []
  const active = data.schedule.filter((event) => !event.removed)
  const groups = scheduleGroups(data)
    .map((group) => ({ ...group, events: group.events.filter((event) => visibleTo(event, parts)) }))
    .filter((group) => group.events.length)
  const shown = groups.reduce((sum, group) => sum + group.events.length, 0)
  const teamOnly = active.filter((event) => event.audience === 'team').length
  const otherParts = active.filter(
    (event) => event.audience === 'guests' && !visibleTo(event, parts),
  ).length
  const { brideName, groomName, date } = data.identity

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-muted-foreground">Zobrazit jako host pozvaný na</span>
        <Select
          aria-label="Zobrazit jako host pozvaný na"
          controlSize="sm"
          className="w-80"
          value={audience?.key ?? wholeDay}
          onValueChange={onAudienceChange}
        >
          {audiences.map((item) => (
            <SelectItem key={item.key} value={item.key}>
              {item.label}
              {item.invitations
                ? ` · ${item.invitations} ${plural(item.invitations, 'pozvánka', 'pozvánky', 'pozvánek')}`
                : ''}
            </SelectItem>
          ))}
        </Select>
        <span className="flex-1" />
        <Link
          to="/svatba"
          className={buttonStyles({ appearance: 'ghost', tone: 'neutral', size: 'sm' })}
        >
          Otevřít svatební web ↗
        </Link>
        {actions}
      </div>

      <Alert tone="info" announcement="none" className="flex items-start gap-2.5">
        <Eye className="mt-1 size-4 shrink-0" aria-hidden="true" />
        <span>
          Host uvidí {shown} z {active.length} událostí. Události jen pro tým ({teamOnly}) se na
          webu neukazují
          {otherParts
            ? `, ${otherParts} ${plural(otherParts, 'další je', 'další jsou', 'dalších je')} z částí dne, na které tahle pozvánka nezve.`
            : '.'}
        </span>
      </Alert>

      <Card className="bg-panel-accent/40 px-5 py-10 sm:px-10 sm:py-14">
        <div className="mx-auto max-w-xl">
          <header className="space-y-2 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-action">
              Program svatebního dne
            </p>
            <h2 className="font-heading text-4xl tracking-tight">
              {brideName} &amp; {groomName}
            </h2>
            {date ? (
              <p className="text-sm text-muted-foreground">{formatDate(date, 'long')}</p>
            ) : null}
          </header>

          {groups.length ? (
            <div className="mt-10 space-y-10">
              {groups.map((group) => (
                <section key={group.id ?? 'prep'} className="space-y-5">
                  <h3 className="flex items-center gap-4 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    <span className="h-px flex-1 bg-border" />
                    {group.label}
                    <span className="h-px flex-1 bg-border" />
                  </h3>
                  <ol className="space-y-6">
                    {group.events.map((event) => (
                      <li key={event.id}>
                        <button
                          type="button"
                          disabled={!onOpen}
                          onClick={() => onOpen?.(event)}
                          className="grid w-full grid-cols-[4.5rem_minmax(0,1fr)] gap-4 rounded-xl p-2 text-left transition-colors enabled:hover:bg-card/70 focus-visible:outline-2 focus-visible:outline-ring sm:grid-cols-[6rem_minmax(0,1fr)]"
                        >
                          <span className="pt-0.5 text-right font-heading text-xl tabular-nums text-action sm:text-2xl">
                            {event.time ?? (
                              <span className="font-sans text-xs text-muted-foreground">
                                Čas upřesníme
                              </span>
                            )}
                          </span>
                          <span className="min-w-0 space-y-1">
                            <span className="block text-base font-semibold">{event.title}</span>
                            {event.endTime ? (
                              <span className="block text-sm text-muted-foreground">
                                do {event.endTime}
                              </span>
                            ) : null}
                            {event.description ? (
                              <span className="block text-sm text-muted-foreground">
                                {event.description}
                              </span>
                            ) : null}
                            {event.location ? (
                              <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                                <MapPin className="size-3.5" aria-hidden="true" />
                                {event.location}
                              </span>
                            ) : null}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ol>
                </section>
              ))}
            </div>
          ) : (
            <p className="mt-10 text-center text-sm text-muted-foreground">
              Pro tuhle pozvánku zatím na webu nic není. Označte události „Hosté i tým“, aby se tu
              objevily.
            </p>
          )}
        </div>
      </Card>
    </div>
  )
}
